import { Card } from '@/components/common';

export type CVTemplate = 'professional' | 'modern' | 'twoColumn' | 'harvard';

interface TemplatePreviewProps {
  template: CVTemplate;
  selected?: boolean;
  onSelect?: () => void;
}

const TEMPLATE_INFO: Record<CVTemplate, { name: string; description: string; features: string[] }> = {
  professional: {
    name: 'Professional',
    description: 'Classic layout with blue left sidebar and traditional styling',
    features: ['Blue gradient left sidebar', 'Traditional single-column layout', 'Bordered section headers', 'Classic professional look'],
  },
  modern: {
    name: 'Modern',
    description: 'Contemporary design with gradient accents and modern typography',
    features: ['Gradient header background', 'Blue gradient skill tags', 'Left border accents', 'Modern sans-serif typography'],
  },
  twoColumn: {
    name: 'Two Column',
    description: 'Two-column design with blue gradient sidebar for one-page CVs',
    features: ['Blue gradient left sidebar', 'Two-column layout', 'Skills in sidebar', 'One-page optimized'],
  },
  harvard: {
    name: 'Harvard',
    description: 'Harvard style with Core Competencies grid',
    features: ['Core competencies grid', 'Clean academic style', 'Professional layout', 'Suitable for MBA/executives'],
  },
};

export function TemplatePreview({ template, selected = false, onSelect }: TemplatePreviewProps) {
  const info = TEMPLATE_INFO[template];

  const renderPreview = () => {
    switch (template) {
      case 'professional':
        return (
          <div className="bg-white text-xs border border-gray-200 rounded overflow-hidden">
            <div className="flex">
              <div className="w-2 bg-gradient-to-b from-blue-600 to-blue-400"></div>
              <div className="flex-1 p-3">
                <div className="mb-2">
                  <div className="text-blue-600 font-bold text-sm mb-1 border-l-4 border-blue-600 pl-2">John Doe</div>
                  <div className="text-gray-500 text-xs">Software Engineer</div>
                  <div className="text-gray-400 text-xs mt-1">email@example.com • +1 234 567 8900</div>
                </div>
                <div className="border-l-4 border-blue-500 pl-2 mb-2">
                  <div className="text-blue-600 font-semibold text-xs uppercase mb-1">Work Experience</div>
                  <div className="space-y-1">
                    <div className="text-gray-700 font-semibold text-xs">Senior Developer • Company</div>
                    <div className="text-gray-500 text-xs">2020 - Present</div>
                    <div className="text-gray-600 text-xs ml-2">• Achievement 1</div>
                  </div>
                </div>
                <div className="border-l-4 border-blue-500 pl-2">
                  <div className="text-blue-600 font-semibold text-xs uppercase mb-1">Skills</div>
                  <div className="flex flex-wrap gap-1">
                    <span className="bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs border border-blue-200">Skill 1</span>
                    <span className="bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs border border-blue-200">Skill 2</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'modern':
        return (
          <div className="bg-white text-xs border border-gray-200 rounded overflow-hidden">
            <div className="mb-2 pb-2 border-b-3 border-blue-500 bg-gradient-to-br from-gray-50 to-white p-3 rounded-t">
              <div className="text-gray-900 font-bold text-sm mb-1">John Doe</div>
              <div className="text-gray-500 text-xs">Software Engineer</div>
              <div className="flex gap-2 text-gray-400 text-xs mt-1">
                <span>email@example.com</span>
                <span>+1 234 567 8900</span>
              </div>
            </div>
            <div className="p-3">
              <div className="mb-2">
                <div className="text-gray-900 font-bold text-xs mb-1 border-l-5 border-blue-500 pl-3 bg-blue-50 py-1 rounded">EXPERIENCE</div>
                <div className="space-y-1 ml-3 border-l-2 border-gray-200 pl-2">
                  <div className="flex justify-between">
                    <div className="text-gray-900 font-semibold text-xs">Senior Developer • Company</div>
                    <div className="text-gray-500 text-xs">2020 - Present</div>
                  </div>
                  <div className="text-gray-600 text-xs ml-2">• Achievement 1</div>
                </div>
              </div>
              <div>
                <div className="text-gray-900 font-bold text-xs mb-1 border-l-5 border-blue-500 pl-3 bg-blue-50 py-1 rounded">SKILLS</div>
                <div className="flex flex-wrap gap-1 ml-3">
                  <span className="bg-gradient-to-br from-blue-500 to-blue-600 text-white px-2 py-0.5 rounded-md text-xs font-medium shadow-sm">Skill 1</span>
                  <span className="bg-gradient-to-br from-blue-500 to-blue-600 text-white px-2 py-0.5 rounded-md text-xs font-medium shadow-sm">Skill 2</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'twoColumn':
        return (
          <div className="bg-white text-xs border border-gray-200 rounded overflow-hidden">
            <div className="text-center mb-2 pb-1 border-b border-gray-200 p-2">
              <div className="text-gray-900 font-bold text-sm mb-0.5">John Doe</div>
              <div className="text-gray-500 text-xs">Software Engineer</div>
              <div className="text-gray-400 text-xs mt-0.5">email@example.com • +1 234 567 8900</div>
            </div>
            <div className="flex gap-0">
              <div className="w-1/3 bg-gradient-to-b from-blue-600 to-blue-500 p-2 text-white">
                <div className="text-white font-semibold text-xs mb-1 border-b border-white/30 pb-0.5">
                  Skills
                </div>
                <div className="text-white/90 text-xs mb-2">Skill 1, Skill 2</div>
                <div className="text-white font-semibold text-xs mb-1 border-b border-white/30 pb-0.5 mt-2">
                  Education
                </div>
                <div className="text-white/90 text-xs">Degree</div>
                <div className="text-white/70 text-xs">Institution</div>
              </div>
              <div className="w-2/3 p-2">
                <div className="mb-2">
                  <div className="text-blue-600 font-semibold text-xs mb-1 border-b border-blue-500 pb-0.5">
                    Summary
                  </div>
                  <div className="text-gray-600 text-xs h-6 bg-gray-50 rounded"></div>
                </div>
                <div>
                  <div className="text-blue-600 font-semibold text-xs mb-1 border-b border-blue-500 pb-0.5">
                    Experience
                  </div>
                  <div className="space-y-1">
                    <div className="text-gray-900 font-semibold text-xs">Senior Developer</div>
                    <div className="text-gray-500 text-xs italic">Company</div>
                    <div className="text-gray-600 text-xs ml-2">• Achievement 1</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'harvard':
        return (
          <div className="bg-white text-xs border border-gray-200 rounded overflow-hidden p-3">
            <div className="text-center mb-2 pb-2 border-b border-gray-300">
              <div className="text-gray-900 font-bold text-sm mb-0.5" style={{ fontFamily: 'serif' }}>John Doe</div>
              <div className="text-gray-500 text-xs">Software Engineer</div>
              <div className="text-gray-400 text-xs mt-0.5">email@example.com • +1 234 567 8900</div>
            </div>
            <div className="mb-2">
              <div className="text-gray-900 font-semibold text-xs mb-1 uppercase tracking-wide">Core Competencies</div>
              <div className="grid grid-cols-3 gap-1">
                <div className="bg-gray-100 text-gray-700 text-xs p-1 rounded text-center">Leadership</div>
                <div className="bg-gray-100 text-gray-700 text-xs p-1 rounded text-center">Strategy</div>
                <div className="bg-gray-100 text-gray-700 text-xs p-1 rounded text-center">Analytics</div>
              </div>
            </div>
            <div className="mb-2">
              <div className="text-gray-900 font-semibold text-xs mb-1 uppercase tracking-wide border-b border-gray-300 pb-0.5">Experience</div>
              <div className="space-y-1">
                <div className="text-gray-900 font-semibold text-xs">Senior Developer • Company</div>
                <div className="text-gray-500 text-xs">2020 - Present</div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card
      className={`cursor-pointer transition-all ${
        selected ? 'ring-2 ring-primary shadow-lg' : 'hover:shadow-md'
      }`}
      onClick={onSelect}
    >
      <div className="p-4 space-y-3">
        {/* Preview */}
        <div className="aspect-[3/4] bg-surface rounded overflow-hidden border border-border">
          <div className="h-full overflow-auto p-2">{renderPreview()}</div>
        </div>

        {/* Template Info */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-text-primary">{info.name}</h3>
            {selected && (
              <svg
                className="w-5 h-5 text-primary"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          <p className="text-sm text-text-secondary mb-2">{info.description}</p>
          <ul className="space-y-1">
            {info.features.map((feature, idx) => (
              <li key={idx} className="text-xs text-text-muted flex items-start">
                <span className="text-primary mr-1">•</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}
